// ─────────────────────────────────────────────────────────────────────────────
// useAnalyticsData — the single fetch pass behind the whole page.
//
// Ten endpoints go out in parallel. Eight of them carry `.catch(() => null)`
// individually, which is the point: a broken trust-score endpoint must dim ONE
// tile, not blank the page. Only the two the page is fundamentally about
// (metrics + trends) can take the whole thing to the error state.
//
// Three distinct states per slice, and they are not interchangeable:
//   null  — not loaded yet          → the tile/chart shows its loading skeleton
//   []    — loaded, nothing to show → "No data"
//   "—"   — asked, backend has no reading (never 0, which would read as a
//           measurement of zero)
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import {
  getDashboardMetrics,
  getDashboardTrends,
  getNodeUtilization,
  getSessionDuration,
  getTrustScore,
  getRouteSuccess,
  getNodeUsageByRegion,
  getTopNodes,
  getDailyActiveUsers,
  getUserActivity,
  rangeToFromDate,
} from "../../../services/dashboardService";
import { dayLabel } from "../format";
import {
  buildTrafficChartData,
  buildRegionsData,
  buildActivityChartData,
  buildNodeUsageData,
  buildTopNodesRows,
} from "../buildAnalytics";

const TOP_NODES_LIMIT = 4;

// Nothing fetched yet — every slice null so each chart shows "Loading…".
const INITIAL = {
  dau: null,          dauDelta: null,
  sessionDur: null,   sessionDurDelta: null,
  nodeUtil: null,     nodeUtilDelta: null,
  routeSuccess: null, routeDelta: null,
  trustScore: null,   trustDelta: null,
  activity: null, traffic: null, nodeUsage: null, topNodes: null, regions: null,
};

// The request pass itself failed. Lists become [] so the charts say "No data"
// rather than sitting on "Loading…" forever.
const FAILED = {
  ...INITIAL,
  nodeUtil: "—",
  activity: [], traffic: [], nodeUsage: [], topNodes: [], regions: [],
};

/** A KPI slice that is only meaningful when the backend sent a real number. */
function numericKpi(raw, format, delta, deltaFallback = 0) {
  return typeof raw === "number"
    ? { value: format(raw), delta: delta ?? deltaFallback }
    : { value: null, delta: null };
}

export function useAnalyticsData() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(INITIAL);

  useEffect(() => {
    let cancelled = false;
    const from = rangeToFromDate("Last 7 Days");
    const to = new Date();

    Promise.all([
      getDashboardMetrics(),
      getDashboardTrends(from, to),
      // Each of these fails alone — one dead endpoint dims one tile or chart.
      getNodeUtilization(from, to).catch(() => null),
      getSessionDuration().catch(() => null),
      getTrustScore().catch(() => null),
      getRouteSuccess().catch(() => null),
      getNodeUsageByRegion().catch(() => null),
      getTopNodes(TOP_NODES_LIMIT).catch(() => null),
      getDailyActiveUsers().catch(() => null),
      getUserActivity().catch(() => null),
    ])
      .then(([metrics, trendsResp, utilResp, durResp, trustResp, routeResp, usageResp, topResp, dauResp, activityResp]) => {
        if (cancelled) return;

        const trends = trendsResp?.trends ?? [];

        // Last complete UTC day's distinct active users + signed % delta vs the
        // day before. A count of 0 is a valid reading; a missing response is not.
        const dau = numericKpi(dauResp?.daily_active_users, (v) => v.toLocaleString(), dauResp?.delta_percent);

        // This week's average session hours + signed hour delta vs last week.
        const dur = numericKpi(durResp?.avg_session_duration_hours, (v) => v.toFixed(1), durResp?.delta_hours);

        // Fleet-wide circuit trust — a 0–100 SCORE, not a percent — + point delta.
        const trust = numericKpi(trustResp?.trust_score, (v) => v.toFixed(1), trustResp?.delta_points);

        // Fleet-wide route success rate (already a percent) + point delta.
        const route = numericKpi(routeResp?.route_success_rate, (v) => `${v.toFixed(1)}%`, routeResp?.delta_points);

        // Server-computed average load. Unlike the four above, "no reading" here
        // prints "—" in the tile rather than falling back to the skeleton.
        const util = utilResp?.current;
        const hasUtil = util && typeof util.value === "number";

        setData({
          dau: dau.value,                 dauDelta: dau.delta,
          sessionDur: dur.value,          sessionDurDelta: dur.delta,
          trustScore: trust.value,        trustDelta: trust.delta,
          routeSuccess: route.value,      routeDelta: route.delta,
          nodeUtil: hasUtil ? `${Math.round(util.value)}%` : "—",
          nodeUtilDelta: hasUtil ? util.delta_percent ?? null : null,

          activity:  buildActivityChartData(activityResp?.trend ?? [], dayLabel),
          traffic:   buildTrafficChartData(trends) ?? [],
          nodeUsage: buildNodeUsageData(usageResp?.regions ?? []),
          topNodes:  buildTopNodesRows(topResp?.nodes ?? []),
          regions:   buildRegionsData(metrics?.traffic_by_region) ?? [],
        });
      })
      .catch(() => { if (!cancelled) setData(FAILED); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { loading, ...data };
}
